import React from "react";
import Typography from "@material-ui/core/Typography";
import {Checkbox, Divider, FormControlLabel, FormGroup, Grid} from "@material-ui/core";
import {OrderEngineStepActions} from "./OrderEngineStepActions";
import {FindAvailabilityStepProps} from "../types";
import {Form, Formik} from "formik";
import * as yup from "yup";
import {InfoOutlined} from "@material-ui/icons";
import {format} from "date-fns";
import {de} from "date-fns/locale";
import {OrderAddress} from "../../features/orders/redux/types";
import {OrderAddressForm} from "../OrderAddressForm";
import {getEmptyOrderAddress} from "../orderAddressState.hook";

declare module "yup" {
    interface StringSchema {
        isValidAddress(format: string): StringSchema;
    }
}

yup.addMethod(yup.string, "isValidAddress", function (errMessage) {
    return this.test('addressNumber', errMessage, function (value) {
        if (value) {
            const posOfFirstComma = value.indexOf(",");
            const potentialNumber = value.substring(posOfFirstComma - 1, posOfFirstComma);

            if (isNaN(parseInt(potentialNumber))) {
                return this.createError({path: this.path, message: errMessage})
            }
        }
        return true
    })
})

const getOrderAddressSchema = () => yup.object().shape({
    name: yup.object().shape({
        salutation: yup.string().required("erforderlich"),
        firstName: yup.string().required("erforderlich").min(2, "zu kurz"),
        lastName: yup.string().required("erforderlich").min(2, "zu kurz")
    }),
    phone: yup.string().required("erforderlich").min(2, "zu kurz")
});

const getInvoiceAddressSchema = () => yup.object().shape({
    name: yup.object().shape({
        salutation: yup.string().required("erforderlich"),
        firstName: yup.string().required("erforderlich").min(2, "zu kurz"),
        lastName: yup.string().required("erforderlich").min(2, "zu kurz")
    }),
    address: yup.object({formattedAddress: yup.string().isValidAddress("Bitte Hausnummer eingeben").required("erforderlich")})
});

const validationSchemaWithInvoice = yup.object({
    serviceAddress: getOrderAddressSchema(),
    invoiceAddress: getInvoiceAddressSchema(),
    email: yup.string().email().required("erforderlich"),
})

const validationSchema = yup.object({
    serviceAddress: getOrderAddressSchema(),
    email: yup.string().email().required("erforderlich"),
})


interface ClientDataStepProps extends FindAvailabilityStepProps<{ serviceAddress: OrderAddress, invoiceAddress?: OrderAddress, email: string; }> {
    estimatedFrom: number;
    estimatedTo: number;
    isDesktop: boolean;
}

export function ClientDataStep(props: ClientDataStepProps) {
    const [serviceAddressNotInvoiceAddress, setServiceAddressNotInvoiceAddress] = React.useState(Boolean(props.initialValue?.invoiceAddress));
    return (
        <div>
            <Typography color={"textSecondary"} style={{marginTop: 16}}>Geben Sie ihre Kontaktdaten ein</Typography>
            <Divider style={{marginBottom: 16, marginTop: 8}}/>
            <Typography variant={props.isDesktop ? "h6" : "subtitle2"} color={"secondary"}
                        style={{marginTop: 16}}>Kontaktdaten</Typography>
            <Typography color={"textSecondary"} variant={props.isDesktop ? "body1" : "body2"} style={{marginTop: 8}}>
                für Ihren Termin
                am <b>{format(props.estimatedFrom, "EEEE, dd.MM.yy", {locale: de})}</b> von <b>{format(props.estimatedFrom, "HH:mm", {locale: de})} bis {format(props.estimatedTo, "HH:mm", {locale: de})} Uhr</b> (geschätzt)
            </Typography>
            <div style={{
                marginBottom: props.isDesktop ? 32 : 16,
                display: "flex",
                marginTop: props.isDesktop ? 24 : 16
            }}>
                <InfoOutlined color="primary"/>
                <Typography variant={props.isDesktop ? "body2" : "caption"} style={{marginLeft: 8}}
                            color="textSecondary">
                    Nach erledigter Arbeit
                    wird die benötigte Zeit
                    (ohne Anfahrt)
                    automatisch erfasst<br/>
                    und Sie erhalten Ihre Rechnung per Post.
                </Typography>
            </div>
            <div style={{display: "flex"}}>
                <Formik
                    enableReinitialize
                    initialValues={{
                        email: props.initialValue?.email || "",
                        serviceAddress: props.initialValue?.serviceAddress ?? getEmptyOrderAddress(),
                        invoiceAddress: props.initialValue?.invoiceAddress
                    }}
                    validationSchema={
                        serviceAddressNotInvoiceAddress
                            ? validationSchemaWithInvoice
                            : validationSchema
                    }
                    onSubmit={props.onNext}
                >
                    {({submitForm, setErrors}) => (
                        <Form style={{width: "100%", flex: 1}}>
                            <Grid container spacing={3} style={{marginBottom: 32}}>
                                <Grid item xs={12} md={6}>
                                    <OrderAddressForm
                                        toolTip="An Diesem Ort wird der Auftrag erledigt"
                                        prefix="serviceAddress"
                                        label={"Einsatzort"}
                                        withEmail
                                        staticAddress={props.initialValue?.serviceAddress.address}
                                    />
                                </Grid>
                                {serviceAddressNotInvoiceAddress && (
                                    <Grid item xs={12} md={6}>
                                        <OrderAddressForm
                                            toolTip="Hierhin wird die Rechnung gesendet"
                                            prefix="invoiceAddress"
                                            label={"Rechnungsaddresse"}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                            <FormGroup aria-label="position" row>
                                <FormControlLabel
                                    value="end"
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={serviceAddressNotInvoiceAddress}
                                        />
                                    }
                                    label={
                                        <Typography variant="inherit">
                                            Der <b>Einsatzort</b> ist nicht die Rechnungsadresse
                                        </Typography>
                                    }
                                    onChange={(_, checked) => {
                                        setServiceAddressNotInvoiceAddress(checked);
                                        setErrors({});
                                    }}
                                    labelPlacement="end"
                                />
                            </FormGroup>
                            <OrderEngineStepActions
                                onClickNext={submitForm}
                                onClickBack={props.onBack}
                                lastStep={false}
                            />
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    )
}
